import { MentionInfo } from "@/lib/mentions";
import { Folder } from "@/types/types";
import { useMemo } from "react";
import { CommandCategoryConfig } from "./CommandPanel";
import { CommandItem } from "./types";

interface UseCategoryGroupsProps {
  folders: Folder[];
  groupFolders: Folder[];
  briefDocumentItems: CommandItem[];
  textDocumentItems: CommandItem[];
  sectionItems: CommandItem[];
  kitFileItems: CommandItem[];
  groupFileItems: CommandItem[];
  kitTemplateItems: CommandItem[];
  groupTemplateItems: CommandItem[];
  libraryItems: CommandItem[];
  skillItems?: CommandItem[];
  recentMentionIds: Map<MentionInfo["id"], MentionInfo>;
  query: string;
}

export const useCategoryGroups = ({
  folders,
  groupFolders,
  briefDocumentItems,
  textDocumentItems,
  sectionItems,
  kitFileItems,
  groupFileItems,
  kitTemplateItems,
  groupTemplateItems,
  libraryItems,
  skillItems = [],
  recentMentionIds,
  query,
}: UseCategoryGroupsProps) => {
  return useMemo(() => {
    const filterItems = (items: CommandItem[]) =>
      items.filter((item) =>
        item.label.toLowerCase().includes(query.toLowerCase()),
      );

    const allFileItems = [...kitFileItems, ...groupFileItems];
    const allDocumentItems = [
      ...briefDocumentItems,
      ...textDocumentItems,
      ...sectionItems,
    ];
    const allTemplateItems = [...kitTemplateItems, ...groupTemplateItems];

    const allItems = [
      ...allFileItems,
      ...allDocumentItems,
      ...allTemplateItems,
      ...libraryItems,
      ...skillItems,
    ];

    const createRecentItems = (): CommandItem[] => {
      const recentItems: CommandItem[] = [];
      [...recentMentionIds.values()].forEach((mentionId) => {
        const type = mentionId.type;
        const uuid = mentionId.id;

        const matchingItem = allItems.find((item) => {
          return (
            item.artifactUuid === uuid &&
            ((type === "file" && item.type === "file") ||
              (type === "document" && item.type === "document") ||
              (type === "template" && item.type === "template") ||
              (type === "library" && item.type === "library") ||
              (type === "skill" && item.type === "skill"))
          );
        });

        if (
          matchingItem &&
          !recentItems.find(
            (item) => item.artifactUuid === matchingItem.artifactUuid,
          )
        ) {
          recentItems.push(matchingItem);
        }
      });

      return recentItems;
    };

    const recentItems = createRecentItems();

    const rootCategories: CommandCategoryConfig[] = [
      {
        key: "all-references" as const,
        label: "All",
        items: filterItems(allItems),
      },
      {
        key: "recently-used" as const,
        label: "Recent",
        items: filterItems(recentItems),
      },
      {
        key: "all-documents" as const,
        label: "Documents",
        items: filterItems(allDocumentItems),
      },
      {
        key: "all-files" as const,
        label: "Knowledge",
        items: filterItems(allFileItems),
      },
      {
        key: "all-templates" as const,
        label: "Templates",
        items: filterItems(allTemplateItems),
      },
      {
        key: "all-skills" as const,
        label: "Skills",
        items: filterItems(skillItems),
      },
      {
        key: "all-library" as const,
        label: "Library",
        items: filterItems(libraryItems),
      },
    ].filter((category) => category.items.length > 0);

    return {
      rootCategories,
      recentItems,
    };
  }, [
    folders,
    groupFolders,
    briefDocumentItems,
    textDocumentItems,
    sectionItems,
    kitFileItems,
    groupFileItems,
    kitTemplateItems,
    groupTemplateItems,
    libraryItems,
    skillItems,
    recentMentionIds,
    query,
  ]);
};
